import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import {
    fetchUserJobs,
} from '../../redux/JobSlice/jobSlice';
import { fetchCompanies } from '../../redux/CompanySlice/companySlice';
import { fetchUserProfile } from '../../redux/AuthSlice/authSlice';
import { fetchSavedJobs, saveMultipleJobs, selectJobStrategy, deleteSavedJob, updateSavedJobStage } from '../../redux/SavedJobSlice/savedJobSlice';
import { fetchSavedCompanies, deleteSavedCompany, updateSavedCompanyStage } from '../../redux/SavedCompanySlice/savedCompanySlice';
import { fetchPerplexitySignals } from '../../redux/HSignalSlice/hsignalSlice';
import { fetchAllContactsByCompanyId, generateReferralStrategy, setCompanyAnalysisComplete } from '../../redux/ContactSlice/contactSlice';
import { fetchRecruiterForJob } from '../../redux/JobSlice/jobSlice';

import api from '../../api/axios';
import Navbaar from '../../components/Navbar/Navbaar';
import { useNavbarActions, useNavbarTitle } from '../../context/NavbarActionsContext';
import Sidebar from '../../components/Navbar/SideNavbar';
import JobNotification from '../../components/common/JobNotification';
import { Rocket, Target, Zap, ArrowRight, LayoutDashboard, TrendingUp, Briefcase, Radio, Trash2, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { STRATEGY_CATALOG } from '../../constants/strategyCatalog';

const PIPELINE_STAGES = [
    { id: 'saved',        name: 'Saved',        color: '#94A3B8' },
    { id: 'applied',      name: 'Applied',      color: '#3F57E0' },
    { id: 'interviewing', name: 'Interviewing', color: '#F59E0B' },
    { id: 'offer',        name: 'Offer',        color: '#10B981' },
];

const SIGNAL_STAGES = [
    { id: 'saved',        name: 'Saved',        color: '#94A3B8' },
    { id: 'applied',      name: 'Applied',      color: '#3F57E0' },
    { id: 'interviewing', name: 'Interviewing', color: '#F59E0B' },
    { id: 'offer',        name: 'Offer',        color: '#10B981' },
];

const SIGNAL_AVATAR_COLORS = [
    'bg-violet-100 text-violet-700', 'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700', 'bg-orange-100 text-orange-700',
    'bg-pink-100 text-pink-700', 'bg-teal-100 text-teal-700',
];

// ── Skeleton loaders for the pipeline board ──────────────────────────
const SavedCardSkeleton = () => (
    <div className="bg-white rounded-2xl border border-[#E6E8EE] p-4 animate-pulse" style={{ boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EEF0F4] shrink-0" />
            <div className="flex-1 min-w-0 space-y-2">
                <div className="h-3.5 w-3/4 bg-[#EAECF2] rounded" />
                <div className="h-3 w-1/2 bg-[#F0F1F5] rounded" />
            </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
            <div className="h-5 w-12 bg-[#F0F1F5] rounded-full" />
            <div className="h-5 w-16 bg-[#F0F1F5] rounded-full" />
            <div className="flex-1" />
            <div className="h-3 w-20 bg-[#F0F1F5] rounded" />
        </div>
    </div>
);

const SavedBoardSkeleton = ({ stages }) => (
    <div className="flex-1 overflow-y-auto overflow-x-hidden p-5">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, alignItems: 'start', width: '100%' }}>
            {stages.map((stage, si) => (
                <div key={stage.id} className="flex flex-col gap-2.5 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 px-1 py-0.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: stage.color }} />
                        <span className="text-[13px] font-bold text-[#0F172A]">{stage.name}</span>
                    </div>
                    {si === 0
                        ? [0, 1, 2, 3].map(i => <SavedCardSkeleton key={i} />)
                        : <div className="rounded-xl border border-dashed border-[#E6E8EE] bg-[#FAFBFD]" style={{ minHeight: 120 }} />}
                </div>
            ))}
        </div>
    </div>
);
const getSignalAvatarColor = (name) => {
    if (!name) return SIGNAL_AVATAR_COLORS[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return SIGNAL_AVATAR_COLORS[Math.abs(h) % SIGNAL_AVATAR_COLORS.length];
};
const getCompanySizeLabel = (size) => {
    if (!size && size !== 0) return null;
    const n = typeof size === 'string'
        ? (size.includes('_') ? parseInt(size.split('_')[0]) : parseInt(size.replace(/[^0-9]/g, '')))
        : size;
    if (isNaN(n) || n === 0) return null;
    if (n <= 10)   return { label: 'Micro',      color: 'bg-pink-100 text-pink-700' };
    if (n <= 50)   return { label: 'Startup',    color: 'bg-purple-100 text-purple-700' };
    if (n <= 200)  return { label: 'Small',      color: 'bg-blue-100 text-blue-700' };
    if (n <= 1000) return { label: 'Growing',    color: 'bg-green-100 text-green-700' };
    if (n <= 5000) return { label: 'Mid-size',   color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Enterprise', color: 'bg-sky-100 text-sky-700' };
};

const BRAND_COLORS = ['#0F172A','#1F4DBE','#5B21B6','#1B6E47','#C2410C','#0D9488','#7C3AED','#B45309'];
const getBrandColor = (name) => BRAND_COLORS[(name || 'C').charCodeAt(0) % BRAND_COLORS.length];

const SavedJobs = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        enrichedJobsArray,
        loading: jobsLoading,
        syncing,
    } = useSelector((state) => state.jobs);

    const { companies } = useSelector((state) => state.companies);
    const { savedJobs, savedJobsLoading, savedJobsError } = useSelector((state) => state.savedJobs);
    const { savedCompanies, loading: savedCompaniesLoading } = useSelector((state) => state.savedCompanies);

    /*
     * Scores for saved PUBLIC companies.
     *
     * The three tiles on a signal card read from HSignals, which only exists for
     * TheirStack companies — so anything saved from Hiring Signals (US) drew
     * three dashes despite those scores being computed and shown on its own
     * detail page. This fills them in from the same source that page uses.
     */
    const [usFit, setUsFit] = useState({});
    const { perplexitySignals } = useSelector((state) => state.hsignal);
    const user = useSelector((state) => state.auth.user);
    const referralStrategies = useSelector((state) => state.contacts.referralStrategies || {});

    const prevSyncing = useRef(syncing);
    const stagesLoadedRef = useRef(false);

    const [showModal, setShowModal] = useState(false);
    const [jobProfileLoading, setJobProfileLoading] = useState(false);
    const [selectedJobTypes, setSelectedJobTypes] = useState([]);
    const [minSalary, setMinSalary] = useState(0);
    const [maxSalary, setMaxSalary] = useState(1000000);
    const [selectedExperience, setSelectedExperience] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [toastMessage, setToastMessage] = useState({ show: false, message: '', type: 'success' });
    const [localLoading, setLocalLoading] = useState(false);
    const [jobsidebarOpen, setJobsidebarOpen] = useState(false);
    const [sortBy, setSortBy] = useState('date');
    /** 'auto' = legacy heuristic per job; otherwise numeric strategy id from STRATEGY_CATALOG */
    const [deployStrategyMode, setDeployStrategyMode] = useState('auto');

    const [activeView, setActiveView] = useState(location.state?.activeView || 'jobs');

    const [pipelineStages, setPipelineStages] = useState({});
    const [draggedJobId, setDraggedJobId]     = useState(null);
    const [dragOverStageId, setDragOverStageId] = useState(null);

    const [signalPipelineStages, setSignalPipelineStages] = useState({});
    const [draggedSignalId, setDraggedSignalId]         = useState(null);
    const [dragOverSignalStageId, setDragOverSignalStageId] = useState(null);

    const [deleteConfirm, setDeleteConfirm]             = useState(null); // { job } | null
    const [deleting, setDeleting]                       = useState(false);
    const [deleteSignalConfirm, setDeleteSignalConfirm] = useState(null); // { sc, name } | null
    const [deletingSignal, setDeletingSignal]           = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const jobsPerPage = 10;
    
    const [initialJobsFetched, setInitialJobsFetched] = useState(false);

    // Scroll/highlight support when navigating from Daily Tasks.
    const consumedScrollSavedJobIdRef = useRef('');
    const attemptedScrollResetRef = useRef(new Set());
    
    // 🆕 NEW STATE for premium system
    const [fetchedCompanies, setFetchedCompanies] = useState([]);
    const [activeRightTab, setActiveRightTab] = useState('insights');
    const processingCompaniesRef = useRef(new Set());
    const requestedStrategiesRef = useRef(new Set());
    const SESSION_STORAGE_KEY = 'cc_saved_recruiter_cards_session';
    
    const getStableJobId = (value) => {
        if (value == null) return '';
        if (typeof value === 'object') {
            if (value._id != null) return String(value._id);
            return String(value);
        }
        return String(value);
    };

    // State for job loading notification
    const [showJobNotification, setShowJobNotification] = useState(false);
    const [jobsLoadedCount, setJobsLoadedCount] = useState(0);
    const prevJobsCount = useRef(0);

    useEffect(() => {
        dispatch(fetchCompanies());
        dispatch(fetchUserProfile());
    }, [dispatch]);

    useEffect(() => {
        if (prevSyncing.current && !syncing) {
            // Fetch the updated jobs list
            dispatch(fetchUserJobs());
        }
        prevSyncing.current = syncing;
    }, [syncing, dispatch]);


    useEffect(() => {
        const uid = user?._id || user?.id;
        if (uid) {
            dispatch(fetchSavedJobs(uid));
            dispatch(fetchSavedCompanies(uid));
            dispatch(fetchPerplexitySignals());
        }
    }, [dispatch, user?._id, user?.id]);

    useEffect(() => {
        if (savedJobs.length > 0) {
            console.log("Saved Jobs: ", savedJobs);
        }
    }, [savedJobs]);
    
    // Effect to track job loading and show notification when saved jobs are loaded
    useEffect(() => {
        // When jobs finish loading and we have jobs
        if (!savedJobsLoading && savedJobs.length > 0) {
            const newJobsCount = savedJobs.length - prevJobsCount.current;
            
            // Only show notification if we had previous jobs or this is not the initial load
            if (initialJobsFetched && newJobsCount > 0) {
                setJobsLoadedCount(newJobsCount);
                setShowJobNotification(true);
                
                // Hide notification after 3 seconds
                const timer = setTimeout(() => {
                    setShowJobNotification(false);
                }, 3000);
                
                return () => clearTimeout(timer);
            }
            
            // Update our reference to the current number of jobs
            prevJobsCount.current = savedJobs.length;
            setInitialJobsFetched(true);
        }
    }, [savedJobsLoading, savedJobs, initialJobsFetched]);

    useEffect(() => {
        if (location.state?.showOnboardingModal) {
            setShowModal(true);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        if (user && companies?.length > 0 && !initialJobsFetched && !syncing) {
            dispatch(fetchUserJobs());
            setInitialJobsFetched(true);
        }
    }, [dispatch, user, companies, initialJobsFetched, syncing]);


    // Load cards from sessionStorage on mount
    useEffect(() => {
        const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                const validCards = parsed
                    .filter(card => card.status === 'complete')
                    .map(card => ({
                        ...card,
                        analysisComplete: true,
                        strategyComplete: true
                    }));
                setFetchedCompanies(validCards);
            } catch (e) {
                console.warn('⚠️ Failed to parse saved session storage:', e);
            }
        }
    }, []);

    // Save cards to sessionStorage whenever they change
    useEffect(() => {
        if (fetchedCompanies.length > 0) {
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(fetchedCompanies));
        } else {
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
    }, [fetchedCompanies]);

    useEffect(() => {
        setFetchedCompanies(prev => {
            let changed = false;
            const updated = prev.map(card => {
                if (!card.organizationId || card.strategyComplete) return card;
                const entry = referralStrategies[card.organizationId];
                if (!entry || entry.loading || (!entry.data && !entry.error)) return card;
                changed = true;
                return { ...card, strategyComplete: true, status: 'complete' };
            });
            return changed ? updated : prev;
        });
    }, [referralStrategies]);

    // Load user-specific pipeline stages once when user ID becomes available
    useEffect(() => {
        const uid = user?._id || user?.id;
        if (!uid) return;
        stagesLoadedRef.current = false; // reset before loading
        // Merge under current state (state wins): this effect re-runs when the user
        // object flips from the JWT shape ({id}) to the profile shape ({_id}), and a
        // plain replace would clobber stages already adopted from the DB.
        try {
            const saved = JSON.parse(localStorage.getItem(`cc_pipeline_stages_${uid}`) || '{}');
            setPipelineStages(prev => ({ ...saved, ...prev }));
        } catch {}
        try {
            const saved = JSON.parse(localStorage.getItem(`cc_signal_pipeline_stages_${uid}`) || '{}');
            setSignalPipelineStages(prev => ({ ...saved, ...prev }));
        } catch {}
        // Mark loaded after state setters are queued
        setTimeout(() => { stagesLoadedRef.current = true; }, 0);
    }, [user?._id, user?.id]);

    // Only save AFTER initial load — depends only on stage state, not user ID
    useEffect(() => {
        if (!stagesLoadedRef.current) return;
        const uid = user?._id || user?.id;
        if (!uid) return;
        localStorage.setItem(`cc_pipeline_stages_${uid}`, JSON.stringify(pipelineStages));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pipelineStages]);

    useEffect(() => {
        if (!stagesLoadedRef.current) return;
        const uid = user?._id || user?.id;
        if (!uid) return;
        localStorage.setItem(`cc_signal_pipeline_stages_${uid}`, JSON.stringify(signalPipelineStages));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalPipelineStages]);

    // Adopt persisted stages from the DB (source of truth) every time the saved
    // list changes; only non-default ('saved') stages override the local map, so
    // any in-flight localStorage positions are preserved. Re-running on every
    // fetch keeps the board correct even if a stale localStorage map was loaded
    // after the first adoption.
    useEffect(() => {
        if (!Array.isArray(savedJobs) || savedJobs.length === 0) return;
        const dbStages = {};
        savedJobs.forEach(sj => {
            const key = getStableJobId(sj.jobId?._id || sj.jobId);
            if (key && sj.stage && sj.stage !== 'saved') dbStages[key] = sj.stage;
        });
        if (Object.keys(dbStages).length > 0) setPipelineStages(prev => ({ ...prev, ...dbStages }));
    }, [savedJobs]);

    useEffect(() => {
        if (!Array.isArray(savedCompanies) || savedCompanies.length === 0) return;
        const dbStages = {};
        savedCompanies.forEach(sc => {
            if (sc?._id && sc.stage && sc.stage !== 'saved') dbStages[String(sc._id)] = sc.stage;
        });
        if (Object.keys(dbStages).length > 0) setSignalPipelineStages(prev => ({ ...prev, ...dbStages }));
    }, [savedCompanies]);

    const getJobStage = (jobId) => pipelineStages[getStableJobId(jobId)] || 'saved';

    const moveJobToStage = (jobId, stageId) => {
        setPipelineStages(prev => ({ ...prev, [getStableJobId(jobId)]: stageId }));
        // Persist to the DB so it survives logout / other devices.
        const targetKey = getStableJobId(jobId);
        const savedEntry = savedJobs.find(sj => getStableJobId(sj.jobId?._id || sj.jobId) === targetKey);
        if (savedEntry?._id) {
            dispatch(updateSavedJobStage({ savedJobId: savedEntry._id, stage: stageId }));
        }
    };

    const handleDragStart = (e, jobId) => {
        setDraggedJobId(jobId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
        setDraggedJobId(null);
        setDragOverStageId(null);
    };

    const handleDragOver = (e, stageId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverStageId !== stageId) setDragOverStageId(stageId);
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOverStageId(null);
    };

    const handleDrop = (e, stageId) => {
        e.preventDefault();
        if (draggedJobId) moveJobToStage(draggedJobId, stageId);
        setDraggedJobId(null);
        setDragOverStageId(null);
    };

    const handleDeleteJob = (e, job) => {
        e.stopPropagation();
        setDeleteConfirm({ job });
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        const savedEntry = savedJobs.find(sj => (sj.jobId?._id || sj.jobId) === deleteConfirm.job._id);
        if (!savedEntry) { setDeleteConfirm(null); return; }
        setDeleting(true);
        await dispatch(deleteSavedJob(savedEntry._id));
        setDeleting(false);
        setDeleteConfirm(null);
    };

    const confirmDeleteSignal = async () => {
        if (!deleteSignalConfirm) return;
        setDeletingSignal(true);
        await dispatch(deleteSavedCompany(deleteSignalConfirm.sc._id));
        setDeletingSignal(false);
        setDeleteSignalConfirm(null);
    };

    const getSignalStage = (id) => signalPipelineStages[String(id)] || 'saved';
    const moveSignalToStage = (id, stageId) => {
        setSignalPipelineStages(prev => ({ ...prev, [String(id)]: stageId }));
        // Persist to the DB (id is the saved-company _id).
        dispatch(updateSavedCompanyStage({ savedCompanyId: id, stage: stageId }));
    };
    const handleSignalDragStart = (e, id) => { setDraggedSignalId(id); e.dataTransfer.effectAllowed = 'move'; };
    const handleSignalDragEnd   = () => { setDraggedSignalId(null); setDragOverSignalStageId(null); };
    const handleSignalDragOver  = (e, stageId) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverSignalStageId !== stageId) setDragOverSignalStageId(stageId); };
    const handleSignalDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverSignalStageId(null); };
    const handleSignalDrop      = (e, stageId) => { e.preventDefault(); if (draggedSignalId) moveSignalToStage(draggedSignalId, stageId); setDraggedSignalId(null); setDragOverSignalStageId(null); };

    const buildContactPayload = (contact) => ({
        name: contact.name,
        title: contact.title,
        department: contact.department || contact._fullData?.department,
        location: contact.location,
        overall_experience_years: contact._fullData?.overall_experience_years,
        company_experience_years: contact._fullData?.company_experience_years,
        summary: contact._fullData?.summary,
        skills: contact._fullData?.skills,
        education: contact._fullData?.education,
        certifications: contact._fullData?.certifications,
        email: contact.email,
        profile_url: contact.profile_url,
        confidence_score: contact.confidence_score,
        referral_probability: contact.referral_probability,
        _fullData: contact._fullData
    });

    const handleAddOrUpdateCompanyCard = async (job) => {
        const companyKey = getStableJobId(job._id);
        const matchingSavedJob = savedJobs.find(sj => sj.jobId?._id === job._id || sj.jobId === job._id);
        const orgId = matchingSavedJob?.organizationId || job.organizationId;

        if (fetchedCompanies.some(c => c.companyKey === companyKey)) {
            setActiveRightTab('recruiters');
            return;
        }

        const newCard = {
            companyKey,
            organizationId: orgId,
            status: 'loading',
            jobData: job,
            analysisComplete: false,
            strategyComplete: false
        };

        setFetchedCompanies(prev => [...prev.filter(c => c.companyKey !== companyKey), newCard]);
        setActiveRightTab('recruiters');

        try {
            const recruiterData = await dispatch(fetchRecruiterForJob({ jobDetails: job })).unwrap();
            const contacts = [
                ...(recruiterData.hiring_manager?.best_guesses || []),
                ...(recruiterData.recruiters?.best_guesses || [])
            ];

            setFetchedCompanies(prev => prev.map(c => 
                c.companyKey === companyKey 
                ? { ...c, recruiterData, analysisComplete: true, status: 'analyzing' } 
                : c
            ));

            if (orgId && contacts.length > 0) {
                const userProfile = user ? {
                    name: user.name,
                    jobTitle: user.jobTitle || user.currentTitle,
                    experience: user.experience,
                    skills: user.skills || [],
                } : {};

                await dispatch(generateReferralStrategy({
                    jobId: job._id,
                    organizationId: orgId,
                    jobDetails: {
                        companyName: job.companyName,
                        jobPosition: job.jobPosition,
                        jobDesc: job.jobDesc
                    },
                    userProfile,
                    allContacts: contacts.map(buildContactPayload)
                })).unwrap();
            }
        } catch (error) {
            console.error('❌ Failed to process company card:', error);
            setFetchedCompanies(prev => prev.filter(c => c.companyKey !== companyKey));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        window.history.replaceState({}, document.title);
    };

    const normalize = (str) => str?.toLowerCase().replace(/[-\s]/g, '');

    const filterJobs = (jobs) => {
        return jobs.filter((job) => {
            const jobTypeNormalized = normalize(job.jobType);
            const matchesType = selectedJobTypes.length === 0 || selectedJobTypes.includes(jobTypeNormalized);

            const salMin = job.jobMinSalary ?? 0;
            const salMax = job.jobMaxSalary ?? Infinity;
            const matchesSalary = 
                job.jobMinSalary == null ||
                job.jobMaxSalary == null ||
                (salMin >= minSalary && salMax <= maxSalary);

            const matchesExperience = 
                selectedExperience.length === 0 ||
                selectedExperience.includes(normalize(job.experienceLevel || ''));

            return matchesType && matchesSalary && matchesExperience;
        });
    };

    const handleCardClick = (job) => {
        navigate(`/jobs/${job._id}`, { state: { fromSavedJobs: true } });
    };

    const handleSaveSelectedJobs = async () => {
        setLocalLoading(true);
        try {
            const savedJobIds = savedJobs.map((job) => job.jobId);
            const newJobsToSave = selectedJobs.filter(
                (job) => !savedJobIds.includes(job._id)
            );

            if (newJobsToSave.length === 0) {
                setToastMessage({ show: true, message: 'All selected jobs are already saved. You are up to date!', type: 'info' });
                return;
            }

            const saved = await dispatch(
                saveMultipleJobs({
                    userId: user._id,
                    jobs: newJobsToSave.map((job) => ({
                        jobId: job._id,
                        companyId: job.companyId,
                        companyName: job.companyName,
                    })),
                })
            ).unwrap();

            const newOrgIds = saved.insertedJobs
                .map(job => job.organizationId)
                .filter(Boolean);

            if (newOrgIds.length > 0) {
                const payload = {
                    preferredIndustry: user.preferredIndustry || [],
                    preferredDepartment: user.preferredDepartment || [],
                    organizationId: newOrgIds,
                };

                await api.post("/api/othercontacts/fetchhiringsignalcontacts", payload);

                console.log("✅ Hiring signal contacts fetched!");
            } else {
                console.log("⚠️ No new organizationIds to fetch hiring signals for.");
            }
        } catch (error) {
            console.error(error);
            setToastMessage({ show: true, message: "An error occurred during saving or fetching contacts.", type: 'error' });
        } finally {
            setLocalLoading(false);
            navigate("/networking")
        }
    };

    const handleDeployStrategies = async () => {
        if (selectedJobs.length === 0) {
            setToastMessage({ show: true, message: "Select at least one saved job to deploy strategy.", type: 'info' });
            return;
        }

        const effectiveUserId = user?._id || user?.id;
        if (!effectiveUserId) {
            setToastMessage({ show: true, message: 'Please sign in to deploy strategies.', type: 'error' });
            return;
        }

        setLocalLoading(true);
        try {
            setActiveRightTab('recruiters');
            setToastMessage({
                show: true,
                message: `Starting strategy deployment for ${selectedJobs.length} selected job${selectedJobs.length > 1 ? 's' : ''}...`,
                type: 'success'
            });

            const uniqueJobs = selectedJobs.filter((job, index, arr) => {
                const id = getStableJobId(job._id);
                return arr.findIndex((j) => getStableJobId(j._id) === id) === index;
            });

            const pickStrategyId = (job) => {
                const postedRaw = job?.datentime || job?.postedAt || job?.job_posted_at_timestamp;
                if (!postedRaw) return 2;
                const postedDate = new Date(postedRaw);
                if (Number.isNaN(postedDate.getTime())) return 2;
                const hoursSincePost = (Date.now() - postedDate.getTime()) / (1000 * 60 * 60);
                return hoursSincePost < 24 ? 3 : 2;
            };

            const resolveStrategyId = (job) => {
                if (deployStrategyMode === 'auto') return pickStrategyId(job);
                const n = Number(deployStrategyMode);
                return Number.isFinite(n) ? n : 2;
            };

            const results = await Promise.allSettled(
                uniqueJobs.map(async (job) => {
                    const jobId = getStableJobId(job?._id);
                    if (!jobId) {
                        throw new Error('Missing job id — try refreshing saved missions.');
                    }
                    const strategyId = resolveStrategyId(job);

                    await dispatch(selectJobStrategy({
                        userId: effectiveUserId,
                        jobId,
                        strategyId
                    })).unwrap();

                    try {
                        await handleAddOrUpdateCompanyCard(job);
                    } catch {
                        /* Recruiter enrichment is optional; strategy is already persisted */
                    }
                })
            );

            const failures = results
                .filter((r) => r.status === 'rejected')
                .map((r) => {
                    const reason = r.reason;
                    if (typeof reason === 'string') return reason;
                    if (reason?.message) return reason.message;
                    return 'Request failed';
                });

            const successCount = results.filter((r) => r.status === 'fulfilled').length;
            const failureCount = failures.length;

            if (successCount > 0) {
                await dispatch(fetchSavedJobs(effectiveUserId));
                setToastMessage({
                    show: true,
                    message: `Strategy deployed for ${successCount} job${successCount > 1 ? 's' : ''}${failureCount ? ` (${failureCount} failed)` : ''}.`,
                    type: failureCount ? 'info' : 'success'
                });
                navigate('/dailytasks');
            } else {
                const detail = failures[0] ? ` ${String(failures[0])}` : '';
                setToastMessage({
                    show: true,
                    message: `Could not deploy strategies.${detail}`,
                    type: 'error'
                });
            }
        } catch (error) {
            console.error(error);
            setToastMessage({ show: true, message: "An error occurred while deploying strategies.", type: 'error' });
        } finally {
            setLocalLoading(false);
        }
    };

    const resolvedSavedJobs = useMemo(() => {
        if (!Array.isArray(savedJobs) || savedJobs.length === 0) return [];

        return savedJobs
            .map((savedItem) => {
                const savedJobId = getStableJobId(savedItem?.jobId?._id || savedItem?.jobId);
                const enrichedMatch = enrichedJobsArray.find((job) => getStableJobId(job?._id) === savedJobId);

                if (enrichedMatch) {
                    return {
                        ...savedItem,
                        jobId: {
                            ...enrichedMatch,
                            _id: enrichedMatch?._id || savedJobId,
                        },
                    };
                }

                return savedItem;
            })
            .filter((savedItem) => savedItem?.jobId);
    }, [savedJobs, enrichedJobsArray]);

    const displayJobs = filterJobs(
        resolvedSavedJobs.map((savedItem) => {
            const job = typeof savedItem.jobId === 'object' ? savedItem.jobId : {};
            const company = job.companyId && typeof job.companyId === 'object' ? job.companyId : null;

            return {
                ...job,
                _id: getStableJobId(savedItem.jobId?._id || savedItem.jobId),
                savedJobId: savedItem._id,
                companyId: job.companyId || savedItem.companyId,
                organizationId: savedItem.organizationId || job.organizationId,
                companyName: job.companyName || company?.name || savedItem.companyName,
                companyLogo: job.companyLogo || company?.companyLogo,
            };
        })
    );

    const paginatedJobs = displayJobs.slice((currentPage - 1) * jobsPerPage, currentPage * jobsPerPage);

    const jobsByStage = useMemo(() => PIPELINE_STAGES.reduce((acc, s) => {
        acc[s.id] = displayJobs.filter(j => getJobStage(j._id) === s.id);
        return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, {}), [displayJobs, pipelineStages]);

    useEffect(() => {
        const tickers = (savedCompanies || [])
            .map((sc) => sc.companyId?.publiclyTradedSymbol)
            .filter(Boolean);
        if (tickers.length === 0) return;

        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post('/api/publicsignals/fit-batch', { tickers });
                if (!cancelled && data?.success) setUsFit(data.scores || {});
            } catch {
                // The board renders without them; dashes are the honest fallback.
            }
        })();
        return () => { cancelled = true; };
    }, [savedCompanies]);

    const signalsByStage = useMemo(() => SIGNAL_STAGES.reduce((acc, s) => {
        acc[s.id] = (savedCompanies || []).filter(sc => getSignalStage(sc._id) === s.id);
        return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, {}), [savedCompanies, signalPipelineStages]);

    useEffect(() => {
        const targetIdRaw = location.state?.scrollSavedJobId;
        if (!targetIdRaw) return;

        const targetId = String(targetIdRaw);
        if (!targetId || consumedScrollSavedJobIdRef.current === targetId) return;

        // Find job index so we can switch to the correct page first.
        const foundIndex = displayJobs.findIndex((job) => getStableJobId(job?._id) === targetId);
        if (foundIndex < 0) {
            // If filters hide the target, clear filters once and retry.
            if (!attemptedScrollResetRef.current.has(targetId)) {
                attemptedScrollResetRef.current.add(targetId);
                setSelectedJobTypes([]);
                setSelectedExperience([]);
                setSelectedCompany(null);
                setMinSalary(0);
                setMaxSalary(1000000);
                setCurrentPage(1);
                return;
            }

            consumedScrollSavedJobIdRef.current = targetId;
            return;
        }

        const targetPage = Math.floor(foundIndex / jobsPerPage) + 1;
        if (currentPage !== targetPage) {
            setCurrentPage(targetPage);
            return;
        }

        requestAnimationFrame(() => {
            const el = document.getElementById(targetId);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('highlight-job-card');
            setTimeout(() => el.classList.remove('highlight-job-card'), 2000);
            consumedScrollSavedJobIdRef.current = targetId;
        });
    }, [location.state, displayJobs, currentPage, jobsPerPage]);

    const handleOpenSidebar = () => {
        setJobsidebarOpen(!jobsidebarOpen);
    }

    // Navbar header (title/subtitle + Jobs/Hiring Signals toggle) — replaces the
    // in-body page header.
    useNavbarTitle(
        {
            title: activeView === 'jobs' ? 'Saved Jobs' : 'Saved Hiring Signals',
            subtitle: activeView === 'jobs'
                ? `${displayJobs.length} job${displayJobs.length !== 1 ? 's' : ''} across 4 stages · pipeline view`
                : `${(savedCompanies || []).length} signal${(savedCompanies || []).length !== 1 ? 's' : ''} across 4 stages · pipeline view`,
        },
        [activeView, displayJobs.length, (savedCompanies || []).length]
    );
    useNavbarActions(
        <div className="flex items-center gap-3">
            {syncing && (
                <div className="hidden lg:flex items-center gap-2 text-[12px] text-[#3F57E0] font-medium">
                    <div className="w-3.5 h-3.5 border-2 border-[#3F57E0] border-t-transparent rounded-full animate-spin" />
                    Syncing…
                </div>
            )}
            <div className="flex items-center bg-[#F1F3F9] rounded-xl p-1 gap-0.5">
                <button
                    onClick={() => setActiveView('jobs')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${activeView === 'jobs' ? 'bg-white text-[#3F57E0] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`}
                >
                    <Briefcase className="w-3.5 h-3.5" />
                    Jobs
                </button>
                <button
                    onClick={() => setActiveView('signals')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${activeView === 'signals' ? 'bg-white text-[#3F57E0] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`}
                >
                    <Radio className="w-3.5 h-3.5" />
                    Hiring Signals
                </button>
            </div>
        </div>,
        [activeView, syncing]
    );

    const jobsInitialLoading = savedJobsLoading && savedJobs.length === 0;
    const signalsInitialLoading = savedCompaniesLoading && (savedCompanies || []).length === 0;

    return (
        <div
            className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col"
        >
            <Navbaar />
            <JobNotification
                show={showJobNotification}
                count={jobsLoadedCount}
                onClose={() => setShowJobNotification(false)}
            />

            <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* Sidebar */}
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    {/* Jobs Kanban board */}
                    {activeView === 'jobs' && jobsInitialLoading ? (
                        <SavedBoardSkeleton stages={PIPELINE_STAGES} />
                    ) : activeView === 'jobs' && savedJobs.length === 0 ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="flex flex-col items-center gap-4 text-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                                    <LayoutDashboard className="w-8 h-8 text-gray-300" />
                                </div>
                                <div>
                                    <p className="text-[15px] font-bold text-[#0F172A]">No saved jobs yet</p>
                                    <p className="text-[12px] text-[#64748B] mt-1">Head over to Jobs to find and save roles.</p>
                                </div>
                                <button
                                    onClick={() => navigate('/')}
                                    className="px-4 py-2 bg-[#3F57E0] text-white text-[13px] font-semibold rounded-lg hover:bg-[#3347C8] transition-colors flex items-center gap-2"
                                >
                                    Go to Jobs <ArrowRight size={14} />
                                </button>
                            </div>
                        </div>
                    ) : activeView === 'jobs' ? (
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-5">
                            <div
                                style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, alignItems: 'start', width: '100%' }}
                            >
                                {PIPELINE_STAGES.map(stage => {
                                    const stageJobs = jobsByStage[stage.id] || [];
                                    return (
                                        <div key={stage.id} className="flex flex-col gap-2.5 min-w-0 overflow-hidden">
                                            {/* Column header */}
                                            <div className="flex items-center gap-2 px-1 py-0.5">
                                                <span
                                                    className="w-2 h-2 rounded-full shrink-0"
                                                    style={{ background: stage.color }}
                                                />
                                                <span className="text-[13px] font-bold text-[#0F172A]">{stage.name}</span>
                                                <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full bg-[#EFF1F5] text-[#64748B]">
                                                    {stageJobs.length}
                                                </span>
                                            </div>

                                            {/* Cards — drop zone */}
                                            <div
                                                className={`flex flex-col gap-2.5 rounded-xl p-1 -m-1 min-h-[60px] transition-all ${dragOverStageId === stage.id ? 'bg-[#EEF1FE]/50 ring-2 ring-[#3F57E0]/25' : ''}`}
                                                onDragOver={e => handleDragOver(e, stage.id)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={e => handleDrop(e, stage.id)}
                                            >
                                                {stageJobs.map((job) => {
                                                    const logo = job.companyLogo && !job.companyLogo.includes('thenounproject') && !job.companyLogo.includes('default') ? job.companyLogo : null;
                                                    const initials = (job.companyName || 'C').charAt(0).toUpperCase();
                                                    const brandColor = getBrandColor(job.companyName);
                                                    const postedRaw = job.datentime || job.postedAt || job.job_posted_at_timestamp;
                                                    const isHot = postedRaw ? (Date.now() - new Date(postedRaw).getTime()) < 48 * 3600000 : false;
                                                    const matchScore = job.matchScore ?? null;
                                                    // Use the match score (0-100) shown on the detail page; fall back to company overall score scaled to 100
                                                    const overallScore = typeof job.score === 'number'
                                                        ? Math.round(job.score)
                                                        : (job.overallScore != null ? Math.round(job.overallScore * 10) : null);
                                                    const fitLabel = overallScore == null ? null
                                                        : overallScore >= 80 ? { text: 'Strong Fit', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
                                                        : overallScore >= 60 ? { text: 'Good Fit',   color: 'bg-blue-50 text-blue-700 border-blue-200' }
                                                        : overallScore >= 40 ? { text: 'Moderate',   color: 'bg-amber-50 text-amber-700 border-amber-200' }
                                                        :                      { text: 'Low Fit',     color: 'bg-red-50 text-red-600 border-red-200' };
                                                    const jobLoc = job.jobLocation || null;
                                                    const card = fetchedCompanies.find(c => c.companyKey === getStableJobId(job._id));
                                                    const contactCount = card?.recruiterData
                                                        ? (card.recruiterData.hiring_manager?.best_guesses?.length || 0) + (card.recruiterData.recruiters?.best_guesses?.length || 0)
                                                        : 0;
                                                    const isDragging = draggedJobId === job._id;

                                                    return (
                                                        <div
                                                            key={job._id}
                                                            draggable
                                                            onDragStart={e => handleDragStart(e, job._id)}
                                                            onDragEnd={handleDragEnd}
                                                            onClick={() => !draggedJobId && handleCardClick(job)}
                                                            className={`bg-white rounded-2xl border border-[#E6E8EE] overflow-hidden cursor-grab active:cursor-grabbing select-none transition-all min-w-0 w-full ${
                                                                isDragging ? 'opacity-40 scale-95 shadow-none' : 'hover:border-[#C7CFFA] hover:shadow-lg'
                                                            }`}
                                                            style={{ boxShadow: isDragging ? 'none' : '0 1px 3px rgba(15,23,42,0.06), 0 2px 8px rgba(15,23,42,0.04)' }}
                                                        >
                                                            {/* Colored accent bar */}
                                                            <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, ${brandColor}, ${brandColor}88)` }} />

                                                            <div className="p-3.5">
                                                                {/* Top row: logo + title + NEW badge */}
                                                                <div className="flex items-start gap-3">
                                                                    {logo ? (
                                                                        <img
                                                                            src={logo}
                                                                            alt={job.companyName}
                                                                            className="w-9 h-9 rounded-xl border border-gray-100 object-cover bg-white shrink-0 shadow-sm"
                                                                            onError={e => { e.target.style.display = 'none'; }}
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-[14px] shrink-0 shadow-sm"
                                                                            style={{ background: brandColor }}
                                                                        >
                                                                            {initials}
                                                                        </div>
                                                                    )}
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-start justify-between gap-1">
                                                                            <p className="text-[12.5px] font-bold text-[#0F172A] leading-snug line-clamp-2 flex-1">
                                                                                {job.jobPosition || job.title || 'Position'}
                                                                            </p>
                                                                            {isHot && (
                                                                                <span className="shrink-0 ml-1 px-1.5 py-0.5 rounded-md text-[9px] font-extrabold tracking-wide bg-emerald-50 text-emerald-600 border border-emerald-200 leading-none">
                                                                                    NEW
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-[11px] text-[#64748B] truncate mt-0.5 font-medium">{job.companyName}</p>
                                                                    </div>
                                                                </div>

                                                                {/* Score + fit + location + delete — single row */}
                                                                <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                                                                    {overallScore != null && (
                                                                        <span className="text-[10px] font-extrabold text-[#3F57E0] leading-none bg-[#EEF1FE] px-2 py-1 rounded-lg">
                                                                            {overallScore}/100
                                                                        </span>
                                                                    )}
                                                                    {fitLabel && (
                                                                        <span className={`px-2 py-1 rounded-lg text-[10px] font-bold border leading-none ${fitLabel.color}`}>{fitLabel.text}</span>
                                                                    )}
                                                                    {jobLoc && (
                                                                        <span className="text-[10px] text-[#94A3B8] truncate leading-none flex-1 min-w-0">{jobLoc}</span>
                                                                    )}
                                                                    {matchScore != null && (
                                                                        <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-[#ECFDF5] text-[#047857] whitespace-nowrap">
                                                                            Match {matchScore}
                                                                        </span>
                                                                    )}
                                                                    {contactCount > 0 && (
                                                                        <span className="flex items-center gap-1 text-[11px] text-[#94A3B8] bg-gray-50 px-2 py-1 rounded-lg">
                                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                                                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                                            </svg>
                                                                            {contactCount}
                                                                        </span>
                                                                    )}
                                                                    {!jobLoc && <span className="flex-1" />}
                                                                    <button
                                                                        onClick={e => handleDeleteJob(e, job)}
                                                                        className="w-6 h-6 flex items-center justify-center rounded-lg text-[#CBD5E1] hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
                                                                        title="Remove from saved"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Drop placeholder — always visible when dragging, always shown if < 3 cards */}
                                                {(stageJobs.length < 3 || (draggedJobId && dragOverStageId === stage.id)) && (
                                                    <div
                                                        className={`rounded-xl border border-dashed px-3 py-3.5 text-center text-[11.5px] font-medium select-none transition-all ${
                                                            dragOverStageId === stage.id
                                                                ? 'border-[#3F57E0] text-[#3F57E0] bg-[#EEF1FE]/40'
                                                                : 'border-[#CBD5E1] text-[#94A3B8]'
                                                        }`}
                                                        style={{ backgroundImage: dragOverStageId === stage.id ? 'none' : 'repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(0,0,0,0.012) 4px,rgba(0,0,0,0.012) 8px)' }}
                                                    >
                                                        {dragOverStageId === stage.id ? `Drop here → ${stage.name}` : '+ Drop job here'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {/* Hiring Signals Kanban */}
                    {activeView === 'signals' && signalsInitialLoading ? (
                        <SavedBoardSkeleton stages={SIGNAL_STAGES} />
                    ) : activeView === 'signals' && (savedCompanies || []).length === 0 ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="flex flex-col items-center gap-4 text-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                                    <Radio className="w-8 h-8 text-gray-300" />
                                </div>
                                <div>
                                    <p className="text-[15px] font-bold text-[#0F172A]">No saved hiring signals yet</p>
                                    <p className="text-[12px] text-[#64748B] mt-1">Head over to Hiring Signals to bookmark companies.</p>
                                </div>
                                <button
                                    onClick={() => navigate('/hiring-signals-us?direction=hiring')}
                                    className="px-4 py-2 bg-[#3F57E0] text-white text-[13px] font-semibold rounded-lg hover:bg-[#3347C8] transition-colors flex items-center gap-2"
                                >
                                    Go to Hiring Signals <ArrowRight size={14} />
                                </button>
                            </div>
                        </div>
                    ) : activeView === 'signals' ? (
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-5">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, alignItems: 'start', width: '100%' }}>
                                {SIGNAL_STAGES.map(stage => {
                                    const stageSignals = signalsByStage[stage.id] || [];
                                    return (
                                        <div key={stage.id} className="flex flex-col gap-2.5 min-w-0 overflow-hidden">
                                            {/* Column header */}
                                            <div className="flex items-center gap-2 px-1 py-0.5">
                                                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stage.color }} />
                                                <span className="text-[13px] font-bold text-[#0F172A]">{stage.name}</span>
                                                <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full bg-[#EFF1F5] text-[#64748B]">
                                                    {stageSignals.length}
                                                </span>
                                            </div>

                                            {/* Cards */}
                                            <div
                                                className={`flex flex-col gap-2.5 rounded-xl p-1 -m-1 min-h-[60px] transition-all ${dragOverSignalStageId === stage.id ? 'bg-[#EEF1FE]/50 ring-2 ring-[#3F57E0]/25' : ''}`}
                                                onDragOver={e => handleSignalDragOver(e, stage.id)}
                                                onDragLeave={handleSignalDragLeave}
                                                onDrop={e => handleSignalDrop(e, stage.id)}
                                            >
                                                {stageSignals.map((sc) => {
                                                    const company = sc.companyId && typeof sc.companyId === 'object' ? sc.companyId : null;
                                                    const name    = company?.name || sc.rawCompanyId || 'Company';
                                                    const logo    = company?.companyLogo && !company.companyLogo.includes('thenounproject') && !company.companyLogo.includes('default') ? company.companyLogo : null;
                                                    // Match this saved company to its HSignals document (which has the real scores)
                                                    const matchedSignal = (perplexitySignals || []).find(ps =>
                                                        (sc.organizationId && ps.organizationId === sc.organizationId) ||
                                                        (String(ps.company?._id || ps.company) === String(sc.companyId?._id || sc.companyId))
                                                    );
                                                    const sigScores    = matchedSignal?.scores || {};
                                                    // A public company has no HSignal, so fall back to the
                                                    // fit we compute from its own filings.
                                                    const usScores     = usFit[company?.publiclyTradedSymbol] || {};
                                                    const hiringScore  = sigScores.hiring_signal_score ?? usScores.hiringScore ?? null;
                                                    const fitScore     = sigScores.skill_fit_score ?? usScores.fitScore ?? null;
                                                    const overallScore = sigScores.overall_score ?? usScores.overallScore ?? null;
                                                    const empCount     = matchedSignal?.employeeCount || company?.size || null;
                                                    const sizeTag      = getCompanySizeLabel(empCount);
                                                    const empFormatted = (() => {
                                                        if (!empCount && empCount !== 0) return null;
                                                        const n = typeof empCount === 'string' ? parseInt(empCount.replace(/[^0-9]/g, '')) : empCount;
                                                        if (isNaN(n) || n === 0) return null;
                                                        if (n >= 1000) return `${(n / 1000) % 1 === 0 ? n / 1000 : (n / 1000).toFixed(1)}K`;
                                                        return n.toLocaleString();
                                                    })();
                                                    const industry     = (matchedSignal?.industry?.[0]) || (Array.isArray(company?.industry) ? company.industry[0] : company?.industry) || null;
                                                    const signalDate   = (() => {
                                                        // savedAt is what SavedCompanies actually stores; a company
                                                        // saved from the US board has no HSignal date behind it.
                                                        const raw = matchedSignal?.createdAt || sc.createdAt || sc.savedAt;
                                                        if (!raw) return null;
                                                        const days = Math.floor((Date.now() - new Date(raw).getTime()) / 86400000);
                                                        if (days === 0) return 'Today';
                                                        if (days === 1) return '1d ago';
                                                        if (days < 7)  return `${days}d ago`;
                                                        const weeks = Math.floor(days / 7);
                                                        if (weeks < 5) return `${weeks}w ago`;
                                                        return `${Math.floor(days / 30)}mo ago`;
                                                    })();
                                                    const isDragging   = draggedSignalId === sc._id;

                                                    return (
                                                        <div
                                                            key={sc._id}
                                                            draggable
                                                            onDragStart={e => handleSignalDragStart(e, sc._id)}
                                                            onDragEnd={handleSignalDragEnd}
                                                            onClick={() => {
                                                                if (draggedSignalId) return;
                                                                /*
                                                                 * A company saved from Hiring Signals (US) carries a
                                                                 * ticker. Its detail lives at /hiring-signals-us/:ticker
                                                                 * — /hiringsignal/:id would 404, since there is no
                                                                 * TheirStack signal behind an SEC filer.
                                                                 */
                                                                const symbol = sc.companyId?.publiclyTradedSymbol;
                                                                if (symbol) { navigate(`/hiring-signals-us/${symbol}`); return; }
                                                                navigate(`/hiringsignal/${sc.rawCompanyId || company?._id}`, { state: { company: matchedSignal || company, fromSavedSignals: true } });
                                                            }}
                                                            className={`bg-white rounded-2xl border border-[#E6E8EE] overflow-hidden cursor-grab active:cursor-grabbing select-none transition-all min-w-0 w-full ${
                                                                isDragging ? 'opacity-40 scale-95 shadow-none' : 'hover:border-[#C7CFFA] hover:shadow-lg'
                                                            }`}
                                                            style={{ boxShadow: isDragging ? 'none' : '0 1px 3px rgba(15,23,42,0.06), 0 2px 8px rgba(15,23,42,0.04)' }}
                                                        >
                                                            {/* Gradient accent bar */}
                                                            <div className="h-[3px] w-full" style={{ background: 'linear-gradient(90deg, #3F57E0, #818CF8)' }} />

                                                            <div className="p-3">
                                                                {/* Header: avatar + name + industry + delete */}
                                                                <div className="flex items-start gap-2">
                                                                    {logo ? (
                                                                        <img src={logo} alt={name} className="w-8 h-8 rounded-lg border border-gray-100 object-cover bg-white shrink-0" onError={e => { e.target.style.display = 'none'; }} />
                                                                    ) : (
                                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-[13px] shrink-0 ${getSignalAvatarColor(name)}`}>
                                                                            {name.charAt(0).toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-[12.5px] font-bold text-[#0F172A] truncate leading-snug">{name}</p>
                                                                        {industry && <p className="text-[10px] text-[#64748B] truncate leading-snug mt-0.5">{industry}</p>}
                                                                    </div>
                                                                    <button
                                                                        onClick={e => { e.stopPropagation(); setDeleteSignalConfirm({ sc, name }); }}
                                                                        className="w-5 h-5 flex items-center justify-center rounded-md text-[#CBD5E1] hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
                                                                        title="Remove from saved"
                                                                    >
                                                                        <Trash2 className="w-3 h-3" />
                                                                    </button>
                                                                </div>

                                                                {/* Size + employee count + date */}
                                                                <div className="flex items-center gap-1.5 mt-1.5">
                                                                    {sizeTag && (
                                                                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold leading-none ${sizeTag.color}`}>{sizeTag.label}</span>
                                                                    )}
                                                                    {empFormatted && (
                                                                        <span className="text-[9.5px] text-[#94A3B8] font-medium">{empFormatted} employees</span>
                                                                    )}
                                                                    {/* What the scores were computed from. A public
                                                                        company's card otherwise showed three numbers
                                                                        with nothing behind them. */}
                                                                    {usScores.signals > 0 && (
                                                                        <span className="text-[9.5px] text-[#94A3B8] font-medium shrink-0">
                                                                            {usScores.signals} signal{usScores.signals === 1 ? '' : 's'}
                                                                            {usScores.documents ? ` · ${usScores.documents} docs` : ''}
                                                                        </span>
                                                                    )}
                                                                    <span className="flex-1" />
                                                                    {signalDate && (
                                                                        <span className="flex items-center gap-0.5 text-[9px] text-[#94A3B8] font-medium shrink-0">
                                                                            <Clock className="w-2.5 h-2.5" />
                                                                            {signalDate}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {/* Divider */}
                                                                <div className="h-px bg-[#F1F3F9] mt-2 mb-2" />

                                                                {/* Score chips */}
                                                                <div className="flex items-stretch gap-1">
                                                                    <div className="flex-1 flex flex-col items-center py-1.5 rounded-lg bg-[#EEF1FE]">
                                                                        <TrendingUp className="w-2.5 h-2.5 text-[#3F57E0] mb-0.5" />
                                                                        <span className="text-[8.5px] text-[#3F57E0] font-medium leading-none mb-0.5">Hiring</span>
                                                                        <span className={`text-[11px] font-extrabold leading-none ${hiringScore != null ? 'text-[#3F57E0]' : 'text-[#A5B4FC]'}`}>
                                                                            {hiringScore != null ? `${hiringScore}/10` : '—'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex-1 flex flex-col items-center py-1.5 rounded-lg bg-emerald-50">
                                                                        <Target className="w-2.5 h-2.5 text-emerald-600 mb-0.5" />
                                                                        <span className="text-[8.5px] text-emerald-600 font-medium leading-none mb-0.5">Your Fit</span>
                                                                        <span className={`text-[11px] font-extrabold leading-none ${fitScore != null ? 'text-emerald-600' : 'text-emerald-300'}`}>
                                                                            {fitScore != null ? `${fitScore}/10` : '—'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex-1 flex flex-col items-center py-1.5 rounded-lg bg-violet-50">
                                                                        <Zap className="w-2.5 h-2.5 text-violet-500 mb-0.5" />
                                                                        <span className="text-[8.5px] text-violet-500 font-medium leading-none mb-0.5">Overall</span>
                                                                        <span className={`text-[11px] font-extrabold leading-none ${overallScore != null ? 'text-violet-600' : 'text-violet-300'}`}>
                                                                            {overallScore != null ? `${overallScore}/10` : '—'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {(stageSignals.length < 3 || (draggedSignalId && dragOverSignalStageId === stage.id)) && (
                                                    <div
                                                        className={`rounded-xl border border-dashed px-3 py-3.5 text-center text-[11.5px] font-medium select-none transition-all ${
                                                            dragOverSignalStageId === stage.id
                                                                ? 'border-[#3F57E0] text-[#3F57E0] bg-[#EEF1FE]/40'
                                                                : 'border-[#CBD5E1] text-[#94A3B8]'
                                                        }`}
                                                        style={{ backgroundImage: dragOverSignalStageId === stage.id ? 'none' : 'repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(0,0,0,0.012) 4px,rgba(0,0,0,0.012) 8px)' }}
                                                    >
                                                        {dragOverSignalStageId === stage.id ? `Drop here → ${stage.name}` : '+ Drop here'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Delete confirmation modal */}
            <AnimatePresence>
                {deleteConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
                        onClick={() => !deleting && setDeleteConfirm(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white rounded-2xl shadow-2xl p-6 w-[340px] mx-4 flex flex-col gap-4"
                        >
                            {/* Icon */}
                            <div className="flex justify-center">
                                <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
                                    <Trash2 className="w-5 h-5 text-red-500" />
                                </div>
                            </div>

                            {/* Text */}
                            <div className="text-center">
                                <p className="text-[15px] font-bold text-[#0F172A]">Remove saved job?</p>
                                <p className="text-[12.5px] text-[#64748B] mt-1 leading-relaxed text-center">
                                    <span className="font-semibold text-[#0F172A]">{deleteConfirm.job.jobPosition || deleteConfirm.job.title || 'This job'}</span>
                                    {' '}at <span className="font-semibold text-[#0F172A]">{deleteConfirm.job.companyName}</span> will be removed from your saved list.
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2.5 mt-1">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    disabled={deleting}
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-[#E6E8EE] text-[13px] font-semibold text-[#64748B] hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={deleting}
                                    className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-[13px] font-semibold hover:bg-red-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                                >
                                    {deleting ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Remove
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Signal delete confirmation modal */}
            <AnimatePresence>
                {deleteSignalConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
                        onClick={() => !deletingSignal && setDeleteSignalConfirm(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white rounded-2xl shadow-2xl p-6 w-[340px] mx-4 flex flex-col gap-4"
                        >
                            <div className="flex justify-center">
                                <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
                                    <Trash2 className="w-5 h-5 text-red-500" />
                                </div>
                            </div>
                            <div className="text-center">
                                <p className="text-[15px] font-bold text-[#0F172A]">Remove hiring signal?</p>
                                <p className="text-[12.5px] text-[#64748B] mt-1 leading-relaxed">
                                    <span className="font-semibold text-[#0F172A]">{deleteSignalConfirm.name}</span> will be removed from your saved signals.
                                </p>
                            </div>
                            <div className="flex gap-2.5 mt-1">
                                <button
                                    onClick={() => setDeleteSignalConfirm(null)}
                                    disabled={deletingSignal}
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-[#E6E8EE] text-[13px] font-semibold text-[#64748B] hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteSignal}
                                    disabled={deletingSignal}
                                    className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-[13px] font-semibold hover:bg-red-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                                >
                                    {deletingSignal ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <><Trash2 className="w-3.5 h-3.5" />Remove</>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toast */}
            <AnimatePresence>
                {toastMessage.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: 50, x: '-50%' }}
                        className={`fixed bottom-8 left-1/2 z-50 px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-white/20 ${
                            toastMessage.type === 'error' ? 'bg-rose-600/90 text-white' : 'bg-emerald-600/90 text-white'
                        }`}
                    >
                        {toastMessage.type === 'error' ? <Target size={16} /> : <Zap size={16} />}
                        <span className="font-semibold text-[13px]">{toastMessage.message}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SavedJobs;
